/**
 * Type definitions for Admin Dashboard UI
 */

// ============================================
// Common Types
// ============================================

/** Type-safe representation of arbitrary JSON data */
export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

/** Type-safe record for metadata and config objects */
export type JsonRecord = Record<string, JsonValue>;

// ============================================
// User Management Types
// ============================================

/**
 * A tenant role: what a person may do inside the tenant they are signed in to.
 * Livepoint's platform admin is not a role; see `AdminUser.platform_admin`.
 */
export type AdminRole = 'admin' | 'operator' | 'viewer';

/**
 * SwiftGate's two-tier identity role. Authorization is owned by Switchboard
 * local RBAC; this type is retained because it appears in OIDC userinfo and
 * the session, but the UI no longer makes any decisions from it.
 */
export type SwiftGateRole = 'admin' | 'user';

/** Numeric hierarchy — higher number = more privileged. Mirrors backend AdminAuthMiddleware. */
export const ROLE_LEVEL: Record<AdminRole, number> = {
  viewer: 0,
  operator: 1,
  admin: 2,
};

/** Mirrors backend hasMinimumRole in src/infra/auth/roleHierarchy.ts. */
export function hasMinimumRole(actor: AdminRole, required: AdminRole): boolean {
  return ROLE_LEVEL[actor] >= ROLE_LEVEL[required];
}

/**
 * Permission string in "resource:action" format, e.g. "user:create", "config:read".
 * Derived from the user's role via RBACProvider on the backend.
 */
export type PermissionString = string;

/** The role a platform admin acts with while switched into a client's tenant. */
export type SwitchRole = 'admin' | 'operator' | 'viewer';

/**
 * The Change tenant rules (database flags in tenant_switch_settings), as the
 * server sends them to a platform admin: the platform's on the session check,
 * each tenant's own on the tenant list (src/services/admin/switchPolicy.ts
 * switchPolicyWire). UX only: the server applies them to every switch.
 */
export interface SwitchPolicy {
  reason_required: boolean;
  reason_min_length: number;
  reason_max_length: number;
  password_required: boolean;
  /** A proof of identity this recent covers further switches; 0 = one per switch. */
  reauth_grace_minutes: number;
  swiftgate_reauth_minutes: number;
  per_hour: number;
  role: SwitchRole;
  switch_minutes: number;
  idle_minutes: number;
}

export interface AdminUser {
  userId: string;
  email: string;
  name: string;
  role: AdminRole;
  permissions: PermissionString[];
  active: boolean;
  tenantSlug?: string | null;
  tenantName?: string | null;
  /**
   * True only for a charged tenant (prepaid, with charging switched on); absent
   * otherwise. The synchronous gate for every billing surface: the usage page
   * and its route, the low-credit banner, the account-menu and landing-page
   * entries. A tenant that is not charged sees none of them.
   * Populated from `/api/admin/auth/session` so every guard has it on first paint.
   */
  billing_visible?: boolean;
  /**
   * This tenant's admins can buy credit (server canBuyCredit: the tenant is
   * charged, CREDIT_PURCHASES_ENABLED is on and Livepoint has switched
   * purchases on for the tenant). Absent or false: no top-up link anywhere —
   * low-credit notices say to contact Livepoint.
   */
  purchases_enabled?: boolean;
  /**
   * The person holds Livepoint's platform admin grant (an out-of-band record,
   * never a tenant role and never an API key). Absent for everyone else. Gates
   * the cross-tenant surfaces: tenant usage and the credit tool, System,
   * Evaluation and Live calls. UX only — every platform route checks the grant
   * itself. Populated from /api/admin/auth/session.
   */
  platform_admin?: boolean;
  /**
   * A platform admin with another tenant to switch to (or switched now).
   * Absent with a single tenancy, so Change tenant does not show at all.
   */
  can_change_tenant?: true;
  /**
   * The platform's rules for Change tenant (a tenant's own row may differ;
   * the tenant list carries those). Sent to platform admins only; the server
   * enforces every one of them.
   */
  switch_policy?: SwitchPolicy;
  /**
   * The local tenants.id the session is in. Sent back on every write as
   * X-Switchboard-Tenant, so a page loaded for one tenant cannot write into
   * another after the session has moved (api/tenantPin.ts).
   */
  tenant_id?: string | null;
  /**
   * Set while a platform admin's session is switched into a client's tenant
   * (they act there as its admin; every action is recorded as Livepoint
   * support). `tenantName` and the billing flags then describe that tenant.
   */
  acting_as_platform_admin?: true;
  /** The tenant the platform admin signed in to, while switched. */
  home_tenant?: { name: string | null };
  /** When the switch ends on its own (ISO), while switched. */
  switch_expires_at?: string | null;
  /**
   * Set while switched: a platform admin may not add, change or remove a
   * tenant's users (that access would outlive the switch), so the user write
   * permissions are left out of `permissions` and the Users page says why.
   */
  user_management_unavailable?: true;
  /**
   * Livepoint has switched the customer API on for this organisation (and it
   * is on at all). Absent otherwise. The Developer API page reads it to offer
   * key creation; listing and revoking existing keys stay available without it.
   * UX only — the key routes check the switch themselves. Populated from
   * /api/admin/auth/session.
   */
  api_access?: boolean;
  /**
   * What this tenant can do with its phone numbers on the Phone numbers page.
   * Absent while Livepoint has not shown the feature to the tenant: the page
   * is the read-only list. Otherwise each action the tenant can see, as `on`
   * or `inactive` (shown, disabled, with a "Contact Livepoint" note); an
   * action left out is not shown at all (buying, for a tenant that is not
   * charged). UX only: every route checks the switches itself. Populated from
   * /api/admin/auth/session.
   */
  numbers?: {
    purchase?: NumberActionState;
    caller_id?: NumberActionState;
    /** Removing a caller ID: as `caller_id`, except that missing number terms do not stop it. */
    caller_id_remove?: NumberActionState;
    give_back?: NumberActionState;
  };
  /**
   * How the user authenticated. Drives the FE logout endpoint choice and any
   * SwiftGate-specific UI affordances. Populated from /api/admin/auth/session.
   */
  authMethod?: 'local' | 'swiftgate' | 'api_key';
  lastLogin?: Date;
  createdAt: Date;
}

/**
 * Check if a permission list includes a specific resource:action.
 * "resource:admin" grants every action on that resource, and only that
 * resource; no permission is a wildcard across resources (mirrors
 * RBACProvider.permissionMatches).
 */
export function hasPermission(permissions: PermissionString[], resource: string, action: string): boolean {
  if (permissions.includes(`${resource}:admin`)) return true;
  return permissions.includes(`${resource}:${action}`);
}

/**
 * Human-readable labels for resource:action permission strings.
 */
export const PERMISSION_LABELS: Record<string, string> = {
  'user:create': 'create users',
  'user:read': 'view users',
  'user:update': 'edit users',
  'user:delete': 'delete users',
  'user:list': 'list users',
  'config:read': 'view configuration',
  'config:update': 'edit configuration',
  'template:admin': 'manage templates',
  'analytics:read': 'view analytics',
  'analytics:export': 'export data',
  'metrics:read': 'view metrics',
  'audit_log:read': 'view logs',
  'api_key:create': 'create API keys',
  'api_key:read': 'view API keys',
  'api_key:update': 'rename API keys',
  'api_key:delete': 'revoke API keys',
  'integration:admin': 'manage integrations',
  'system:execute': 'clear cache',
  'email:admin': 'manage emails',
  'draft:admin': 'manage drafts',
  'agent:admin': 'manage agents',
};

export interface UserProfile extends AdminUser {
  /** Widened from the literal 'swiftgate' so local-auth users have a valid value.
   *  AdminUser already declares authMethod as optional; UserProfile makes it required. */
  authMethod: 'local' | 'swiftgate' | 'api_key';
  swiftgateSub: string | null;
  externalId: string | null;
  tenantId: string | null;
  emailVerified: boolean;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  updatedAt: string | null;
  createdBy?: { name: string; email: string } | null;
}

export interface User {
  userId: string;
  email: string;
  name: string;
  role: AdminRole;
  /** Effective permissions derived server-side from the user's role via RBACProvider. */
  permissions: PermissionString[];
  active: boolean;
  mfaEnabled: boolean;
  emailVerified: boolean;
  /** True once the user has completed their first SG login for this tenant. */
  setupCompleted: boolean;
  swiftgateSub?: string;
  externalId?: string | null;
  tenantId?: string | null;
  tenantSlug?: string | null;
  createdAt: string;
  updatedAt?: string;
  lastLogin?: string | null;
  createdBy?: { name: string; email: string } | null;
}

export interface CreateUserInput {
  email: string;
  name?: string;
  role?: AdminRole;
}

/**
 * Which link Switchboard emailed a local-auth user: `invite` (the long-lived
 * set-up link, for a user who has never signed in) or `reset` (the short-lived
 * password-reset link, for one who has). The server decides from
 * `users.last_login_at`; the UI only reports what was sent.
 */
export type IssuedLinkKind = 'invite' | 'reset';

/**
 * Returned by POST /admin/users when Switchboard itself emailed the invite
 * (local-auth admins). Absent on the SwiftGate path, where SwiftGate sends its
 * own invite. `ttl_minutes` is the configured lifetime the email also quotes.
 */
export interface InviteIssued {
  expires_at: string;
  ttl_minutes: number;
  kind: IssuedLinkKind;
}

/**
 * Returned by the two authenticated password-reset routes
 * (POST /admin/users/:userId/password-reset and POST /admin/auth/profile/password-reset).
 * `kind` and `ttl_minutes` are present only when Switchboard minted and
 * emailed the link itself; a SwiftGate-backed target is handed to SwiftGate's
 * own emailer, which owns its lifetime. The self-service profile route always
 * sends a reset and reports no `kind`.
 */
export interface PasswordResetIssued {
  message: string;
  kind?: IssuedLinkKind;
  ttl_minutes?: number;
}

export interface CreateUserResult extends User {
  /** False when the email already belonged to a user who was added to this tenant. */
  created: boolean;
  invite?: InviteIssued;
}

export interface UpdateUserInput {
  email?: string;
  role?: AdminRole;
  active?: boolean;
}

export interface UserFilter {
  role?: AdminRole | string;
  status?: 'active' | 'inactive' | 'invite_pending';
  search?: string;
  sort_by?: 'email' | 'display_name' | 'role' | 'status' | 'created_at' | 'last_login';
  sort_dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface PaginatedUsersResponse {
  users: User[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================
// Customer API keys (the Developer API page; GET/POST /admin/api-keys)
// ============================================

/** 'custom' is a key whose stored scopes match neither level (written some other way). */
export type ApiKeyLevel = 'read' | 'calls' | 'custom';
export type ApiKeyStatus = 'active' | 'expiring' | 'expired' | 'revoked' | 'rotating';
export type ApiKeyExpiryChoice = '90d' | '1y' | 'custom';
/** How long the old key keeps working after a rotation; 0 stops it at once. */
export type RotationOverlapHours = 0 | 1 | 24 | 168;

/** One key as the list shows it. The secret is never part of it. */
export interface ApiKeyListing {
  id: string;
  name: string;
  /** e.g. `sbk_live_ab12…9f3c`; older keys show only the head. */
  masked_key: string;
  level: ApiKeyLevel;
  scopes: string[];
  status: ApiKeyStatus;
  /** When a rotated key stops working; set only while status is 'rotating'. */
  rotating_until: string | null;
  rotated_to_key_id: string | null;
  /** Null once the person who made the key has been deleted. */
  created_by: { id: string; name: string | null } | null;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  /** 'platform' when Livepoint revoked the key; null while it is not revoked. */
  revoked_by: 'platform' | 'tenant' | null;
}

/**
 * The limits in force. The call limits, calling window and destinations are
 * present only while the API is on; `daily_credits` only for a charged tenant.
 */
export interface ApiKeyLimits {
  max_active_keys: number;
  active_keys: number;
  concurrent_calls?: number;
  daily_calls?: number;
  calls_per_number_per_day?: number;
  calls_per_minute?: number;
  requests_per_minute?: number;
  calling_window?: { days: string[]; start: string; end: string; timezone: string };
  destination_countries?: string[];
  daily_credits?: number;
}

export interface ApiKeysResponse {
  /** Livepoint has switched the API on for this organisation (and it is on at all). */
  api_enabled: boolean;
  keys: ApiKeyListing[];
  limits: ApiKeyLimits;
}

export interface CreateApiKeyInput {
  name: string;
  level: 'read' | 'calls';
  expires_in: ApiKeyExpiryChoice;
  /** A date (YYYY-MM-DD) when expires_in is 'custom'. */
  expires_at?: string;
}

/** A create's answer: the full key is in `secret`, in this response only. */
export interface IssuedApiKey {
  key: ApiKeyListing;
  secret: string;
}

export interface RotatedApiKey extends IssuedApiKey {
  previous_key: ApiKeyListing;
}

// ============================================
// Analytics Types
// ============================================

export interface SystemMetrics {
  performance: {
    avgResponseTime: number;
    p50ResponseTime: number;
    p95ResponseTime: number;
    p99ResponseTime: number;
    requestsPerSecond: number;
    errorRate: number;
  };
  resources: {
    cpuUsage: number;
    memoryUsage: number;
    diskUsage: number;
    activeConnections: number;
  };
  calls: {
    totalCalls: number;
    activeCalls: number;
    completedCalls: number;
    failedCalls: number;
    avgCallDuration: number;
    avgWaitTime: number;
  };
  emails: {
    totalDrafts: number;
    pendingDrafts: number;
    approvedDrafts: number;
    sentEmails: number;
    rejectedDrafts: number;
    autoApprovalRate: number;
    avgConfidenceScore: number;
  };
  intelligence: {
    classificationsPerHour: number;
    extractionsPerHour: number;
    avgClassificationTime: number;
    avgExtractionTime: number;
    cacheHitRate: number;
  };
  users: {
    totalUsers: number;
    activeUsers: number;
    dailyActiveUsers: number;
    avgSessionDuration: number;
  };
}

export interface BusinessMetrics {
  efficiency: {
    automationRate: number;
    timeSavedHours: number;
    costSavings: number;
    humanInterventionRate: number;
  };
  quality: {
    customerSatisfaction: number;
    avgResolutionTime: number;
    firstContactResolution: number;
    escalationRate: number;
  };
  volume: {
    dailyInteractions: number;
    weeklyInteractions: number;
    monthlyInteractions: number;
    growthRate: number;
  };
  categories: {
    categoryBreakdown: Record<string, number>;
    urgencyBreakdown: Record<string, number>;
    sentimentBreakdown: Record<string, number>;
  };
}

export interface MetricDataPoint {
  timestamp: Date;
  value: number;
  metadata?: JsonRecord;
}

export interface TimeSeries {
  name: string;
  dataPoints: MetricDataPoint[];
}

export interface Report {
  reportId: string;
  name: string;
  type: 'system' | 'business' | 'custom';
  createdAt: Date;
  createdBy: string;
  filters: ReportFilter;
  data: JsonValue;
  summary: string;
}

export interface ReportFilter {
  startDate: Date;
  endDate: Date;
  granularity?: 'hour' | 'day' | 'week' | 'month';
  categories?: string[];
  users?: string[];
  includeDetails?: boolean;
}

// ============================================
// Audit Log Types
// ============================================

export type AuditLogLevel = 'INFO' | 'WARN' | 'ERROR' | 'SECURITY';

export type AuditAction =
  | 'user.create'
  | 'user.update'
  | 'user.delete'
  | 'user.login'
  | 'user.logout'
  | 'user.role_change'
  | 'apikey.create'
  | 'apikey.revoke'
  | 'apikey.view'
  | 'config.update'
  | 'config.rollback'
  | 'config.export'
  | 'config.import'
  | 'config.reset'
  | 'system.start'
  | 'system.stop'
  | 'system.restart'
  | 'cache.clear'
  | 'data.read'
  | 'data.write'
  | 'data.delete'
  | 'data.export'
  | 'security.access_denied'
  | 'security.permission_denied'
  | 'security.rate_limit'
  | 'security.suspicious_activity'
  | 'other';

/**
 * Who did an audit entry, as the customer view sends it: one of the tenant's
 * own people (email and IP), or Livepoint support (the reason given, never
 * the staff member's identity).
 */
export type AuditLogActor =
  | { kind: 'user'; email: string | null; ipAddress: string | null }
  | { kind: 'livepoint_support'; reason: string | null };

/**
 * One audit entry as the tenant's audit-log routes return it (the server's
 * customer view, core/presentation/customerAuditView). `action_label` is the
 * action in plain words; `action` stays the stored code.
 */
export interface AuditLogEntry {
  logId: string;
  timestamp: string;
  level: AuditLogLevel;
  action: string;
  action_label: string;
  resource: string;
  resourceId: string | null;
  success: boolean;
  actor: AuditLogActor;
}

/** One entry's detail view: its details, changes and error, scrubbed on the server. */
export interface AuditLogDetail extends AuditLogEntry {
  details: JsonRecord | null;
  changes: { before?: JsonValue; after?: JsonValue } | null;
  error: string | null;
}

export interface AuditLogFilter {
  level?: AuditLogLevel | AuditLogLevel[];
  action?: AuditAction | AuditAction[];
  resource?: string;
  userId?: string;
  success?: boolean;
  startDate?: Date;
  endDate?: Date;
  search?: string;
  tags?: string[];
  limit?: number;
  offset?: number;
}

export interface AuditLogStats {
  totalLogs: number;
  logsByLevel: Record<AuditLogLevel, number>;
  logsByAction: Record<string, number>;
  failureRate: number;
  topActors: Array<{ userId: string; count: number }>;
  topActions: Array<{ action: AuditAction; count: number }>;
  recentSecurityEvents: number;
}

// ============================================
// Dashboard Types
// ============================================

export interface DashboardStats {
  system: {
    uptime: number;
    version: string;
    environment: string;
    health: 'healthy' | 'degraded' | 'down';
  };
  activity: {
    activeCalls: number;
    pendingDrafts: number;
    activeUsers: number;
    recentAlerts: number;
  };
  performance: {
    cpuUsage: number;
    memoryUsage: number;
    errorRate: number;
    avgResponseTime: number;
  };
}

export interface Alert {
  alertId: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  timestamp: Date;
  resolved: boolean;
  resolvedAt?: Date;
  resolvedBy?: string;
}

// ============================================
// Agent Types
// ============================================

export type EmailReasoningFramework = 'default' | 'star';

export interface Agent {
  /** The agent's id: what a call records as its agent, and the Dashboard filters by. */
  id?: string;
  name: string;
  displayName?: string;
  description?: string;
  active: boolean;
  channels: AgentChannel[];
  phoneNumbers?: string[];
  callerPhoneNumbers?: string[];
  /** The stored "Calls out from" number, or null for the answering number. */
  outboundCallerId?: string | null;
  /**
   * The number this agent's next outbound call (with no from_number) would
   * present, as the server's call rule works it out; null when it would be
   * refused, or when the server couldn't read the account's numbers.
   */
  effectiveOutboundCallerId?: string | null;
  /**
   * Why that call would be refused: the stored "Calls out from" is no longer
   * assigned, or there is nothing to call from. Null with a null
   * effectiveOutboundCallerId means the numbers couldn't be read: unknown,
   * not fine.
   */
  outboundCallerIdProblem?: 'not_assigned' | 'no_number' | null;
  /** Answers people who ring back after it called them. The server sends false when unset. */
  answerCallbacks?: boolean;
  /** How long after its call a ring-back still counts, in minutes. The server sends 10080 (7 days) when unset. */
  callbackWindowMinutes?: number;
  /** Followed only after a caller confirms a callback; absent when empty. */
  callbackInstructions?: string;
  /** How many of the earlier call's last lines a confirmed callback is given. The server sends 40 when unset. */
  callbackTranscriptLines?: 0 | 20 | 40 | 80 | 'all';
  /** Whether a confirmed callback is told what the earlier call did. The server sends true when unset. */
  callbackIncludeActions?: boolean;
  emailAccounts?: string[];
  systemPrompt?: string;
  purpose?: string;
  guardrails?: string;
  context?: string;
  provider?: AgentProviderConfig;
  voice?: AgentVoiceConfig;
  verification?: AgentVerificationConfig;
  tools?: AgentToolConfig[];
  initialData?: AgentInitialDataConfig;
  notifications?: AgentNotificationConfig;
  amdConfig?: AgentAmdConfig;
  bargeInConfig?: AgentBargeInConfig;
  /** The call behaviour settings this agent sets, by group; absent ones follow the platform default. */
  callBehaviour?: AgentCallBehaviourStored;
  postConversationHooks?: PostConversationHook[];
  emailReasoningFramework?: EmailReasoningFramework;
  /** The version the agent runs now: each save that changes it adds one. */
  version?: number;
  createdAt?: Date;
  updatedAt?: Date;
  metadata?: JsonRecord;
}

export type AgentChannel = 'voice' | 'webchat' | 'sms' | 'whatsapp' | 'email';

export interface AgentProviderConfig {
  provider: 'openai' | 'anthropic';
  model?: string;
  fallbackProviders?: string[];
  temperature?: number;
  maxTokens?: number;
}

export interface AgentVoiceConfig {
  voiceId?: string;
  welcomeMessage?: string;
  goodbyeMessage?: string;
  recordingEnabled?: boolean;
  transferNumber?: string;
  amdEnabled?: boolean;
  /** No filler phrases ("One sec…", "Okay…") while the agent thinks; for IVR navigation (default: false) */
  silentMode?: boolean;
  /** Maximum call duration in seconds (0, null, or undefined = the default (600 s); values above 600 are clamped) */
  maxCallDurationSeconds?: number | null;
  /** Warning message to speak before ending call due to time limit */
  maxCallDurationWarningMessage?: string | null;
  /** Wait for human greeting before playing welcome message on outbound calls (default: true) */
  waitForGreeting?: boolean;
  /** Timeout in ms to wait for greeting before playing welcome message anyway (default: 8000) */
  greetingWaitTimeoutMs?: number;
}

/** AMD action when answering machine is detected */
export type AgentAmdAction = 'continue' | 'hangup' | 'leave_message';

/** AMD configuration for outbound calls */
export interface AgentAmdConfig {
  /** Whether AMD is enabled for this agent (overrides global setting) */
  enabled?: boolean;
  /** What action to take when an answering machine is detected */
  action?: AgentAmdAction;
  /** Message to leave on voicemail (only used when action is 'leave_message') */
  voicemailMessage?: string;
}

/** An agent's call behaviour groups (src/core/callBehaviour/catalogue.ts), each the settings it sets. */
export type AgentCallBehaviourGroup = 'endCall' | 'interruptions' | 'greeting' | 'voicemail' | 'silence' | 'stt';
export type AgentCallBehaviourStored = Partial<Record<AgentCallBehaviourGroup, Record<string, unknown>>>;
export type AgentCallBehaviourInput = Partial<Record<AgentCallBehaviourGroup, Record<string, unknown> | null>>;

/** Barge-in sensitivity preset */
export type BargeInSensitivity = 'sensitive' | 'balanced' | 'patient' | 'custom';

/** Per-agent barge-in / interruption tuning */
export interface AgentBargeInConfig {
  sensitivity?: BargeInSensitivity;
  debounceMs?: number;
  graceMs?: number;
  cooldownMs?: number;
  interruptThresholdDbfs?: number;
  confirmationFrames?: number;
}

export interface AgentVerificationConfig {
  required: boolean;
  methods: string[];
  maxAttempts?: number;
  timeoutMs?: number;
}

export interface AgentToolConfig {
  name: string;
  enabled: boolean;
  config?: JsonRecord;
  /** Preserved even though the current form does not edit advanced prompt guidance. */
  guidance?: JsonRecord;
}

/** Credential status for per-agent tool configuration */
export interface AgentToolCredentialStatus {
  hasStoredCredentials: boolean;
  credentials: Record<string, string>;  // Masked values like "****1234"
}

/** Response from credential endpoints */
export interface ToolCredentialsResponse {
  success: boolean;
  agentName?: string;
  toolName?: string;
  hasStoredCredentials?: boolean;
  credentials?: Record<string, string>;
  message?: string;
  error?: string;
}

/** Request body for setting credentials */
export interface SetToolCredentialsRequest {
  credentials: Record<string, string>;
}

/** Request body for testing connection */
export interface TestToolConnectionRequest {
  subdomain?: string;
  api_key?: string;
  workflow_id?: string;
}

/** Response from connection test */
export interface TestToolConnectionResponse {
  success: boolean;
  message: string;
}

// ============================================
// Tenant Phone Number Types
// ============================================

/**
 * `twilio`: a number on Livepoint's Twilio account — agents can answer on it.
 * `verified_caller_id`: an office line verified with Twilio — outbound calls
 * can show it, but it cannot receive calls.
 */
export type TenantPhoneNumberType = 'twilio' | 'verified_caller_id';

/**
 * An agent that uses the number: it answers on it (one of its dialled
 * numbers) or calls out from it (its "Calls out from"). One agent can appear
 * twice, once per use.
 */
export interface PhoneNumberAgentUse {
  agent_id: string;
  agent_name: string;
  /** The name to show people, when the agent has one; else show agent_name. */
  agent_display_name: string | null;
  tenant_id: string;
  use: 'answers' | 'calls_out_from';
  active: boolean;
}

/** One phone number action as the session carries it: usable, or shown but switched off. */
export type NumberActionState = 'on' | 'inactive';

/**
 * Where one of the customer's numbers is in its life: in use, its monthly
 * charge not taken (with the date it goes), being given back (with that date),
 * still being bought, or a caller ID waiting for its verification call.
 */
export type PhoneNumberStatus = 'active' | 'overdue' | 'release_scheduled' | 'pending_purchase' | 'pending_verification';

/** What a number is charged, in whole credits. Absent for a number with no charge. */
export interface PhoneNumberCharge {
  monthly_credits: number;
  next_charge_at: string | null;
  paid_through: string | null;
  /** A new monthly charge the customer has had notice of, from a date. */
  pending: { monthly_credits: number; from: string } | null;
}

/** A number Livepoint has assigned to the customer. */
export interface TenantPhoneNumber {
  id: string;
  tenant_id: string;
  /** E.164, e.g. +441514538810. */
  phone_number: string;
  number_type: TenantPhoneNumberType;
  /** The customer's own name for the number; null until given. */
  name: string | null;
  /** The customer's own notes on the number; may span lines. Null until given. */
  description: string | null;
  /** A factual description worked out from the number, e.g. "UK landline (0151)". */
  default_description: string;
  created_at: string;
  /** Answering agents first, then the agents that call out from it. */
  used_by: PhoneNumberAgentUse[];
  /** Whether an active agent answers on it; false means a callback reaches no agent. */
  answered_by_active_agent: boolean;
  /** Where the number is in its life (the customer list sends it; Livepoint's lists may not). */
  status?: PhoneNumberStatus;
  /** When an overdue number, or one being given back, goes. */
  release_at?: string | null;
  /** Its monthly charge, when it has one. */
  charge?: PhoneNumberCharge | null;
}

/** One kind of number a tenant can buy, and its price. */
export interface NumberKindOffer {
  kind: 'local' | 'national' | 'mobile' | 'toll_free';
  monthly_credits: number;
  /** The first month plus what must be left to take calls. */
  needed_credits: number;
}

/** GET /admin/phone-numbers/purchase-options. */
export interface NumberPurchaseOptions {
  kinds: NumberKindOffer[];
  balance_credits: number;
  max_results: number;
}

/** A number Twilio will sell now. */
export interface AvailablePhoneNumber {
  phone_number: string;
  kind: NumberKindOffer['kind'];
  locality: string | null;
  description: string;
}

/** The number terms an admin accepts before buying a number or adding a caller ID. */
export interface NumberTerms {
  version: string;
  text: string;
}

/** A caller ID verification just started: show the code once. */
export interface CallerIdVerificationStarted {
  phone_number: TenantPhoneNumber;
  validation_code: string;
  expires_at: string;
}

/** A number given back, or kept after all. */
export interface GivenBackPhoneNumber {
  released: boolean;
  phone_number: { id: string; phone_number: string; status: string; release_at: string | null };
}

/**
 * A customer's edit of a number's details. A field left out is kept; null or
 * blank clears it.
 */
export interface UpdatePhoneNumberDetailsInput {
  name?: string | null;
  description?: string | null;
}

/** One field's change in a number's history. */
export interface PhoneNumberDetailChange {
  from: string | null;
  to: string | null;
}

/**
 * One line of a number's history (GET /admin/phone-numbers/:id/history).
 * `added_before_history` is the start the server adds when the number was
 * added before history was kept, or by a direct insert; its `who` is null.
 * `livepoint_support` marks a Livepoint platform admin switched into the
 * customer. `via` is how the change was made, e.g. `admin_session` or
 * `provisioning_token`.
 */
export interface PhoneNumberHistoryEntry {
  when: string;
  event:
    | 'assigned'
    | 'unassigned'
    | 'details_changed'
    | 'released'
    | 'give_back_scheduled'
    | 'give_back_undone'
    | 'release_held'
    | 'charge_set'
    | 'added_before_history';
  changes?: { name?: PhoneNumberDetailChange; description?: PhoneNumberDetailChange };
  /** `give_back_scheduled` and `release_held`: the date the number goes. */
  release_at?: string | null;
  /** `charge_set`: the monthly charge, in whole credits. */
  monthly_charge_credits?: number | null;
  /** `assigned`: the number was bought. */
  purchased?: boolean;
  who: { name: string | null; email: string | null; livepoint_support: boolean } | null;
  via: string | null;
}

/** One page of a number's history, newest first. */
export interface PhoneNumberHistoryPage {
  entries: PhoneNumberHistoryEntry[];
  total: number;
  page: number;
  page_size: number;
}

// ============================================
// Integration Profile Types
// ============================================

export interface IntegrationProfileSummary {
  id: string;
  name: string;
  integration_type: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  hasCredentials: boolean;
  config: JsonRecord | null;
  created_at: string;
  updated_at: string;
}

export interface IntegrationProfileDetail {
  id: string;
  name: string;
  integration_type: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  config: JsonRecord | null;
  hasCredentials: boolean;
  maskedCredentials?: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface CreateIntegrationProfileInput {
  name: string;
  description?: string;
  is_default?: boolean;
  config?: JsonRecord;
  credentials?: Record<string, string>;
}

export interface UpdateIntegrationProfileInput {
  name?: string;
  description?: string;
  is_default?: boolean;
  is_active?: boolean;
  config?: JsonRecord;
  credentials?: Record<string, string>;
}

export interface IntegrationProfilesResponse {
  success: boolean;
  profiles: IntegrationProfileSummary[];
}

export interface IntegrationProfileResponse {
  success: boolean;
  profile: IntegrationProfileDetail;
}

export interface TestIntegrationConnectionRequest {
  profile_id?: string;
  subdomain?: string;
  api_key?: string;
  workflow_id?: string;
  /** SwiftCase host domain to probe (production `swiftcase.co.uk` or UAT `swift-case.co.uk`) */
  domain?: string;
}

export interface TestIntegrationConnectionResponse {
  success: boolean;
  message: string;
}

export interface AgentInitialDataConfig {
  workflowId?: string;
  clientStaffId?: string;
  customFields?: JsonRecord;
}

export interface AgentNotificationConfig {
  webhookUrl?: string;
  webhookHeaders?: Record<string, string>;
  notificationEmails?: string[];
  includeTranscript?: boolean;
  includeCollectedData?: boolean;
}

// ============================================
// Post-Conversation Hook Types
// ============================================

/** Base hook properties shared by all hook types */
interface BaseHook {
  id: string;
  name: string;
  enabled: boolean;
  /** Fire-and-forget (true) or wait for completion (false) */
  async?: boolean;
  /** Condition expression - hook only runs if truthy (future) */
  condition?: string;
}

/** Execute a registered tool with interpolated payload */
export interface ToolHook extends BaseHook {
  type: 'tool';
  toolName: string;
  /** Payload template - supports {{placeholders}} */
  payload: JsonRecord;
}

/** POST data to an external webhook */
export interface WebhookHook extends BaseHook {
  type: 'webhook';
  url: string;
  method?: 'POST' | 'PUT' | 'PATCH';
  headers?: Record<string, string>;
  /** Body template - supports {{placeholders}} */
  body: JsonRecord;
  /** Timeout in milliseconds */
  timeoutMs?: number;
  /** Retry count */
  retries?: number;
}

/** Run an LLM prompt and store result (e.g., for summarization) */
export interface ClosingPromptHook extends BaseHook {
  type: 'closing_prompt';
  /** System prompt for the LLM - defines the assistant's role/behavior */
  systemPrompt: string;
  /** User prompt template - supports {{placeholders}} */
  prompt: string;
  /** Where to store the result in hook context */
  outputVariable: string;
  /** Run before other hooks (to make result available) */
  runFirst?: boolean;
  /** Response format - 'text' for plain text, 'json' for structured data */
  responseFormat?: 'text' | 'json';
  /** JSON schema for structured output (when responseFormat is 'json') */
  jsonSchema?: JsonSchema;
  /** Override agent's max tokens setting */
  maxTokens?: number;
  /** Override agent's temperature setting */
  temperature?: number;
  /** Override agent's model setting */
  model?: string;
  /** Override agent's provider setting */
  provider?: 'openai' | 'anthropic';
}

/** JSON Schema for structured LLM output */
export interface JsonSchema {
  type: 'object';
  properties: Record<string, JsonSchemaProperty>;
  required?: string[];
}

export interface JsonSchemaProperty {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description?: string;
  enum?: string[];
  items?: JsonSchemaProperty;
}

/** All hook types (discriminated union) */
export type PostConversationHook = ToolHook | WebhookHook | ClosingPromptHook;

/** Hook type identifiers */
export type HookType = PostConversationHook['type'];

export interface CreateAgentInput {
  name: string;
  displayName?: string;
  description?: string;
  active?: boolean;
  channels?: AgentChannel[];
  phoneNumbers?: string[];
  callerPhoneNumbers?: string[];
  /** "Calls out from"; null clears it (the answering number is used). */
  outboundCallerId?: string | null;
  answerCallbacks?: boolean;
  /** Minutes; null puts back the default (7 days). */
  callbackWindowMinutes?: number | null;
  /** null clears them. */
  callbackInstructions?: string | null;
  /** null puts back the default (40 lines). */
  callbackTranscriptLines?: 0 | 20 | 40 | 80 | 'all' | null;
  /** null puts back the default (on). */
  callbackIncludeActions?: boolean | null;
  purpose?: string;
  guardrails?: string;
  context?: string;
  voice?: AgentVoiceConfig;
  provider?: AgentProviderConfig;
  verification?: AgentVerificationConfig;
  tools?: AgentToolConfig[];
  initialData?: AgentInitialDataConfig | null;
  notifications?: AgentNotificationConfig | null;
  amdConfig?: AgentAmdConfig | null;
  bargeInConfig?: AgentBargeInConfig | null;
  /** Each group replaces the stored one whole; null clears a group, or every group. */
  callBehaviour?: AgentCallBehaviourInput | null;
  postConversationHooks?: PostConversationHook[];
  emailReasoningFramework?: EmailReasoningFramework;
}

export interface UpdateAgentInput {
  displayName?: string;
  description?: string;
  active?: boolean;
  channels?: AgentChannel[];
  phoneNumbers?: string[];
  callerPhoneNumbers?: string[];
  /** "Calls out from"; null clears it (the answering number is used). */
  outboundCallerId?: string | null;
  answerCallbacks?: boolean;
  /** Minutes; null puts back the default (7 days). */
  callbackWindowMinutes?: number | null;
  /** null clears them. */
  callbackInstructions?: string | null;
  /** null puts back the default (40 lines). */
  callbackTranscriptLines?: 0 | 20 | 40 | 80 | 'all' | null;
  /** null puts back the default (on). */
  callbackIncludeActions?: boolean | null;
  purpose?: string;
  guardrails?: string;
  context?: string;
  voice?: AgentVoiceConfig;
  provider?: AgentProviderConfig;
  verification?: AgentVerificationConfig;
  tools?: AgentToolConfig[];
  initialData?: AgentInitialDataConfig | null;
  notifications?: AgentNotificationConfig | null;
  amdConfig?: AgentAmdConfig | null;
  bargeInConfig?: AgentBargeInConfig | null;
  /** Each group replaces the stored one whole; null clears a group, or every group. */
  callBehaviour?: AgentCallBehaviourInput | null;
  postConversationHooks?: PostConversationHook[];
  emailReasoningFramework?: EmailReasoningFramework;
  /** The version the editor opened, recorded on the version this save writes. */
  baseVersion?: number;
}

// ============================================
// Agent versions (the agent editor's Versions tab)
// ============================================

/** The editor tab a changed setting lives on, or 'other'. */
export type AgentVersionTab =
  | 'basic' | 'routing' | 'channels' | 'conversation' | 'llm' | 'voice'
  | 'advancedVoice' | 'callBehaviour' | 'verification' | 'tools' | 'initialData'
  | 'notifications' | 'hooks' | 'other';

/** Where a version came from. */
export type AgentVersionSource = 'dashboard' | 'restore' | 'provisioning' | 'baseline' | 'deleted' | 'system';

export interface AgentVersionChangedField {
  key: string;
  tab: AgentVersionTab;
  label: string;
}

/** One row of an agent's Versions list. */
export interface AgentVersionListItem {
  version: number;
  createdAt: string;
  source: AgentVersionSource;
  restoredFromVersion: number | null;
  baseVersion: number | null;
  note: string | null;
  /** Null for a version the system wrote (a baseline, provisioning). */
  actor: { name: string | null; isSupport: boolean } | null;
  changedFields: AgentVersionChangedField[];
  /** The version the agent runs now. */
  isCurrent: boolean;
}

export interface AgentVersionPage {
  versions: AgentVersionListItem[];
  total: number;
  page: number;
  page_size: number;
  /** The version the agent runs now; null until it has a saved version. */
  currentVersion: number | null;
}

export interface AgentVersionLineDiffPart {
  op: 'equal' | 'add' | 'remove';
  text: string;
}

/** One setting that differs between two versions; credentials come masked. */
export interface AgentVersionChange extends AgentVersionChangedField {
  before: JsonValue | undefined;
  after: JsonValue | undefined;
  /** For long free text (the prompt, the welcome message): the lines removed and added. */
  lineDiff?: AgentVersionLineDiffPart[];
}

/** What a compare is against: the version before, or the live agent. */
export type AgentVersionCompareTarget = 'previous' | 'current';

export interface AgentVersionComparison {
  /** The older side, or null when the newer side is the first version. */
  from: number | null;
  /** The newer side: a version, or 'current' for the live agent. */
  to: number | 'current';
  changes: AgentVersionChange[];
}

export interface AgentVersionRestoreResult {
  /** The agent as it now runs. */
  agent: Agent;
  /** The live version after the restore (unchanged when nothing differed). */
  version: number;
  restoredFromVersion: number;
}

// ============================================
// API Response Types
// ============================================

export interface ApiError {
  success: false;
  error: string;
  message?: string;
}

export interface ApiSuccess<T> {
  success: true;
  data?: T;
  [key: string]: T | boolean | JsonValue | undefined;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

// ============================================
// Evaluation & A/B Testing Types
// ============================================

export interface ABTest {
  testId: string;
  name: string;
  description?: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  variants: ABTestVariant[];
  startedAt?: Date;
  endedAt?: Date;
  createdAt: Date;
  createdBy: string;
  winningVariant?: string;
  minSampleSize: number;
  confidenceLevel: number;
}

export interface ABTestVariant {
  variantId: string;
  name: string;
  promptConfig: JsonRecord;
  metrics: ABTestMetrics;
  sampleSize: number;
  isControl: boolean;
}

export interface ABTestMetrics {
  totalInteractions: number;
  avgResponseTime: number;
  successRate: number;
  avgConfidence: number;
  userSatisfaction?: number;
  escalationRate: number;
}

export interface EvaluationFeedback {
  feedbackId: string;
  conversationId: string;
  rating: number;
  helpful: boolean;
  comment?: string;
  category?: string;
  timestamp: Date;
}

export interface ConversationQuality {
  conversationId: string;
  dimensions: {
    responseRelevance: number;
    taskCompletion: number;
    clarity: number;
    politeness: number;
    efficiency: number;
    accuracy: number;
  };
  overallScore: number;
  flags: string[];
  analyzedAt: Date;
}

export interface FailedConversation {
  conversationId: string;
  timestamp: Date;
  reason: string;
  category: string;
  transcript: Array<{ role: string; content: string }>;
  priority: 'low' | 'medium' | 'high' | 'critical';
  resolved: boolean;
  resolution?: string;
}

export interface EvaluationStats {
  totalConversations: number;
  avgQualityScore: number;
  feedbackCount: number;
  avgRating: number;
  helpfulRate: number;
  failedConversations: number;
  activeABTests: number;
  qualityTrend: 'improving' | 'stable' | 'declining';
}

// ============================================
// Audio Quality & Call Monitoring Types
// ============================================

export interface AudioQualityMetrics {
  callId: string;
  sessionId: string;
  timestamp: Date;
  avgRms: number;
  avgDbfs: number;
  peakDbfs: number;
  speechPercentage: number;
  estimatedSnr: number;
  clippingPercentage: number;
  silencePercentage: number;
  overallQuality: number;
  bargeInCount: number;
  interruptionCount: number;
}

export interface ActiveCall {
  callId: string;
  sessionId: string;
  callerNumber: string;
  agentName: string;
  startedAt: Date;
  duration: number;
  state: 'ringing' | 'connected' | 'speaking' | 'listening' | 'processing' | 'transferring' | 'ended';
  direction: 'inbound' | 'outbound';
  audioQuality: {
    snr: number;
    quality: number;
    issues: string[];
  };
  transcriptPreview?: string;
}

export interface CallQualityHistory {
  timestamp: Date;
  snr: number;
  quality: number;
  dbfs: number;
  isSpeaking: boolean;
  bargeIn: boolean;
}

export interface AudioSystemStats {
  activeCalls: number;
  avgQuality: number;
  avgSnr: number;
  totalBargeIns: number;
  totalInterruptions: number;
  callsWithIssues: number;
  qualityDistribution: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
  };
}

// ============================================
// Guardrails & Safety Types
// ============================================

export interface GuardrailEvent {
  eventId: string;
  timestamp: Date;
  type: 'injection_detected' | 'pii_detected' | 'escalation_triggered' | 'content_blocked';
  conversationId: string;
  details: JsonRecord;
  action: 'blocked' | 'masked' | 'escalated' | 'logged';
  severity: 'low' | 'medium' | 'high';
}

export interface GuardrailStats {
  totalEvents: number;
  eventsByType: Record<string, number>;
  piiDetections: number;
  injectionAttempts: number;
  escalations: number;
  blockedInputs: number;
}

// ============================================
// Email Template Types
// ============================================

export interface EmailTemplateVariable {
  name: string;
  required: boolean;
  description?: string;
  defaultValue?: string;
}

export interface EmailTemplate {
  id: string;
  templateId: string;
  name: string;
  description: string | null;
  category: string;
  subjectTemplate: string;
  bodyTemplate: string;
  bodyHtmlTemplate: string | null;
  variables: EmailTemplateVariable[] | null;
  isActive: boolean;
  isSystem: boolean;
  priority: number;
  metadata: JsonRecord | null;
  tags: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailTemplateVersion {
  id: string;
  templateId: string;
  version: number;
  subjectTemplate: string;
  bodyTemplate: string;
  bodyHtmlTemplate: string | null;
  variables: EmailTemplateVariable[] | null;
  changedBy: string | null;
  changeReason: string | null;
  createdAt: string;
}

export interface CreateEmailTemplateInput {
  templateId: string;
  name: string;
  description?: string;
  category?: string;
  subjectTemplate: string;
  bodyTemplate: string;
  bodyHtmlTemplate?: string;
  variables?: EmailTemplateVariable[];
  isActive?: boolean;
  isSystem?: boolean;
  priority?: number;
  metadata?: JsonRecord;
  tags?: string[];
}

export interface UpdateEmailTemplateInput {
  name?: string;
  description?: string;
  category?: string;
  subjectTemplate?: string;
  bodyTemplate?: string;
  bodyHtmlTemplate?: string;
  variables?: EmailTemplateVariable[];
  isActive?: boolean;
  priority?: number;
  metadata?: JsonRecord;
  tags?: string[];
  changeReason?: string;
}

export interface EmailTemplatePreview {
  subject: string;
  bodyText: string;
  bodyHtml?: string;
  metadata?: JsonRecord;
}

// ============================================
// Voice Configuration Types
// ============================================

export type TTSProviderType =
  | 'elevenlabs'
  | 'aws_polly'
  | 'google_tts'
  | 'azure_tts'
  | 'openai_tts'
  | 'custom';

/** Provider-specific settings with discriminated union for type safety */
export type TTSProviderSettings =
  | { provider: 'elevenlabs'; apiKey?: string; modelId?: string; optimizeLatency?: boolean }
  | { provider: 'aws_polly'; region?: string; engine?: 'standard' | 'neural'; }
  | { provider: 'google_tts'; projectId?: string; speakingRate?: number; }
  | { provider: 'azure_tts'; subscriptionKey?: string; region?: string; }
  | { provider: 'openai_tts'; apiKey?: string; responseFormat?: string; }
  | { provider: 'custom'; endpoint?: string; headers?: Record<string, string>; };

/**
 * One entry in a provider-populated picker (models, voices). `id` is what gets
 * stored; `label` is what the admin reads — Anthropic supplies a display name,
 * OpenAI and ElevenLabs do not always, in which case the id is reused.
 */
export interface CatalogOption {
  id: string;
  label: string;
  /** Server-owned policy metadata used when the form includes live voice. */
  suitableForVoice?: boolean;
  recommendedForVoice?: boolean;
  voiceSuitabilityReason?: string;
}

/**
 * A voice from the ElevenLabs account. Gender, accent and the preview clip
 * are present only when ElevenLabs labels the voice (its library voices);
 * custom and cloned voices usually have none. The preview is a public sample
 * clip, so playing it spends no credits.
 */
export interface VoiceOption extends CatalogOption {
  gender?: string;
  /** Lower-case: "british", "american", "australian"… */
  accent?: string;
  previewUrl?: string;
}

/**
 * One row of GET /admin/config/llm-providers: whether this server holds an
 * API key for the provider. The agent form uses it to grey out a fallback
 * provider the server cannot honour, instead of offering it as if it worked.
 */
export interface LlmProviderAvailability {
  id: string;
  name: string;
  configured: boolean;
}

export interface VoiceConfig {
  id: string;
  agentId: string | null;
  name: string;
  description?: string;
  provider: TTSProviderType;
  voiceIdentifier: string;
  model?: string;
  stability?: number;
  similarityBoost?: number;
  style?: number;
  speed?: number;
  pitch?: number;
  outputFormat?: string;
  sampleRate?: number;
  languageCode?: string;
  providerSettings?: TTSProviderSettings;
  active: boolean;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateVoiceConfigInput {
  agentId?: string;
  name: string;
  description?: string;
  provider: TTSProviderType;
  voiceIdentifier: string;
  model?: string;
  stability?: number;
  similarityBoost?: number;
  style?: number;
  speed?: number;
  pitch?: number;
  outputFormat?: string;
  sampleRate?: number;
  languageCode?: string;
  providerSettings?: TTSProviderSettings;
  isDefault?: boolean;
}

export interface UpdateVoiceConfigInput {
  name?: string;
  description?: string;
  provider?: TTSProviderType;
  voiceIdentifier?: string;
  model?: string;
  stability?: number;
  similarityBoost?: number;
  style?: number;
  speed?: number;
  pitch?: number;
  outputFormat?: string;
  sampleRate?: number;
  languageCode?: string;
  providerSettings?: TTSProviderSettings;
  active?: boolean;
  isDefault?: boolean;
}

export interface TTSProvider {
  id: TTSProviderType;
  name: string;
  implemented: boolean;
}

// ============================================
// Conversation Types
// ============================================

export type ConversationChannel = 'voice' | 'email' | 'sms' | 'chat';
export type ConversationStatus = 'active' | 'completed' | 'transferred' | 'failed' | 'abandoned';
export type ConversationDirection = 'inbound' | 'outbound';

/**
 * The outcome groups a customer sees and filters by. Their words come from
 * the server (outcome_label, ConversationStats.outcomeLabels), never from here.
 */
export type OutcomeCategory =
  | 'completed'
  | 'ended'
  | 'max_duration'
  | 'no_audio'
  | 'voicemail'
  | 'out_of_credit'
  | 'technical_error'
  | 'transferred'
  | 'other';

/**
 * A conversation as the tenant's conversation routes return it (the server's
 * customer view, core/presentation/customerConversationView). The stored
 * outcome code, recording URL and SID, stream id and metadata are not sent;
 * the outcome and status come with their plain-words labels.
 */
export interface Conversation {
  id: string;
  tenant_id: string;
  agent_id: string | null;
  agent_name: string | null;
  channel: ConversationChannel;
  direction: ConversationDirection;
  status: ConversationStatus;
  status_label: string;
  caller_phone: string | null;
  caller_email: string | null;
  caller_name: string | null;
  call_sid: string | null;
  email_message_id: string | null;
  email_thread_id: string | null;
  email_subject: string | null;
  collected_slots: JsonRecord | null;
  initial_data: JsonRecord | null;
  summary: string | null;
  /** Null while the conversation has no outcome yet. */
  outcome_category: OutcomeCategory | null;
  outcome_label: string | null;
  transfer_destination: string | null;
  message_count: number;
  turn_count: number;
  duration_seconds: number | null;
  sentiment_score: number | null;
  satisfaction_score: number | null;
  started_at: string;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  has_recording: boolean;
  /** Who ended the call (agent, caller, caller_during_goodbye, system, voicemail, error, handover, transfer); null on older calls. */
  ended_by?: ConversationEndedBy | null;
  /** Who ended the call and why, in plain English; null when no end was recorded. */
  end_reason_label?: string | null;
  /** Why this agent answered (dashboard only); null when the call doesn't say, such as an outbound call. */
  routing_reason?: ConversationRoutingReason | null;
  /** On an outbound call: what the person hears if they ring back now; null when a ring-back wouldn't be a callback. */
  callback_question?: string | null;
}

/** Why an inbound call reached its agent, in the server's words. */
export interface ConversationRoutingReason {
  kind: 'dialled_number' | 'caller_list' | 'callback' | 'handover';
  sentence: string;
  /** The caller's answer to the callback question. */
  decision: 'confirmed' | 'declined' | 'unclear' | null;
  /** The call this one is about: the outbound call a callback returns, or the call a handover came from. */
  linked_conversation_id: string | null;
  /** Shown in place of the link when there is no call to open ("That call wasn't answered."). */
  link_note: string | null;
}

/** Who said what and when; tool calls and their results stay on the server. */
export interface ConversationMessage {
  id: string;
  role: 'user' | 'agent' | 'system';
  content: string;
  sequence: number;
  created_at: string;
  /** The tools the line used, by name; their arguments and results stay on the server. */
  tool_names: string[];
  /** Milliseconds from the start of the call to this line; null when the line has no timing. */
  offset_ms?: number | null;
  /** The caller spoke over this agent line, so it was cut off. */
  interrupted?: boolean;
}

export type ConversationEndedBy =
  | 'agent' | 'caller' | 'caller_during_goodbye' | 'system' | 'voicemail' | 'error' | 'handover' | 'transfer';

/** One event a call recorded (call_events); the payload's fields depend on the type. */
export interface ConversationDiagnosticsEvent {
  stream_index: number;
  seq: number;
  type: 'end_attempt' | 'end_outcome' | 'barge_in' | 'turn' | 'detector_verdict' | 'reprompt' | 'resume' | 'second_opinion';
  payload: Record<string, unknown> | null;
  created_at: string;
}

/** A call's internal record, for Livepoint's platform admins only. */
export interface ConversationDiagnostics {
  conversation_id: string;
  end: {
    ended_by: ConversationEndedBy | null;
    end_reason_code: string | null;
    outcome: string | null;
    summary: string | null;
  };
  events: ConversationDiagnosticsEvent[];
}

/** Whether a recording can be played; the file itself comes through the download route. */
export interface ConversationRecording {
  available: boolean;
  format: 'mp3' | 'wav';
}

export interface ConversationFilter {
  agent_name?: string;
  channel?: ConversationChannel;
  status?: ConversationStatus;
  caller_phone?: string;
  /** An outcome category (the server rejects anything else). */
  outcome?: OutcomeCategory;
  started_after?: string;
  started_before?: string;
}

export interface ConversationStats {
  total: number;
  /** Keyed by stored status; statusLabels holds each one's words. */
  byStatus: Record<string, number>;
  byChannel: Record<string, number>;
  /** Keyed by outcome category, never by stored code; outcomeLabels holds the words. */
  byOutcome: Partial<Record<OutcomeCategory, number>>;
  outcomeLabels: Partial<Record<OutcomeCategory, string>>;
  statusLabels: Record<string, string>;
  avgDuration: number;
  avgMessages: number;
}

/**
 * The groups a call can end in on the Dashboard, in the server's order
 * (src/core/presentation/customerOutcomeGroups.ts, OUTCOME_GROUP_CODES). A
 * tenant is sent only the ones it can see, each with its words in `groups`;
 * the code is a key, never shown.
 */
export type OutcomeGroupCode =
  | 'completed'
  | 'transferred'
  | 'caller_hung_up'
  | 'agent_ended'
  | 'voicemail'
  | 'no_audio'
  | 'max_duration'
  | 'out_of_credit'
  | 'account_limit'
  | 'ended'
  | 'not_recorded'
  | 'other';

/** Calls in each group the tenant is shown, and all calls. */
export type OverviewTotals = { calls: number } & Partial<Record<OutcomeGroupCode, number>>;

/**
 * GET /api/admin/conversations-overview: the tenant's phone calls over a
 * period of UK days and the period of equal length before it. A reconnect or
 * a handover is one call. Counts and customer words only.
 */
export interface ConversationsOverview {
  /** Both ends inclusive, YYYY-MM-DD UK days. */
  period: { from: string; to: string };
  previousPeriod: { from: string; to: string };
  groups: Array<{ code: OutcomeGroupCode; label: string }>;
  totals: OverviewTotals;
  previousTotals: OverviewTotals;
  /**
   * Completed or transferred, out of the calls a conversation could happen on
   * that have a recorded ending. `notRecorded`: calls left out of the
   * denominator because nothing recorded why they ended.
   */
  headline: { numerator: number; denominator: number; notRecorded: number };
  /**
   * Calls under 20 seconds, out of the calls a conversation could happen on
   * (every call but voicemail and no audio, answered or not, with a recorded
   * ending or not: the headline's denominator plus headline.notRecorded).
   */
  shortCalls: { count: number; of: number };
  byDay: Array<{ date: string } & Partial<Record<OutcomeGroupCode, number>>>;
  /** 7 rows (Monday first) of 24 hours, UK time. */
  byHourWeekday: number[][];
  /** Why calls ended, in the words Conversations uses, with the group each is counted in. */
  byReason: Array<{ label: string; group: OutcomeGroupCode; count: number }>;
  /** Mean seconds of Completed calls with a known duration; null with none. */
  avgDurationCompleted: number | null;
  /** When the tenant was created (ISO), so a comparison with a period before it is not offered. */
  tenantCreatedAt: string | null;
  /**
   * Older calls with no recorded ending that are counted in a group sorted
   * from their call transcripts. Already in the counts above; the page says
   * how many there were.
   */
  estimatedCalls: number;
}

/** What the Dashboard asks the overview for. */
export interface ConversationsOverviewQuery {
  from: string;
  to: string;
  /** One agent's calls; absent for every agent. */
  agentId?: string;
}

export interface PaginatedConversations {
  data: Conversation[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
